import React, { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib";
import { TokenBadge } from "./shared";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui";

const AppLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  const displayName = useMemo(() => {
    if (!user?.email) return "Guest";
    const [name] = user.email.split("@");
    return name.charAt(0).toUpperCase() + name.slice(1);
  }, [user?.email]);

  const isAdmin = useMemo(() => {
    const roles = user?.app_metadata?.roles;
    return Array.isArray(roles) && roles.includes("admin");
  }, [user?.app_metadata]);

  useEffect(() => {
    let active = true;
    if (!user?.id) {
      setTokenBalance(null);
      return undefined;
    }
    setLoadingBalance(true);
    supabase
      .from("user_wallets")
      .select("token_balance")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          console.error("[wallet] failed to load token balance", error);
          setTokenBalance(null);
          return;
        }
        setTokenBalance(data?.token_balance ?? 0);
      })
      .finally(() => {
        if (active) {
          setLoadingBalance(false);
        }
      });

    return () => {
      active = false;
    };
  }, [user?.id]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error("[auth] Failed to logout", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const navItems = [
    {
      to: "/dashboard",
      label: "Magic Studio",
      emoji: "🎨",
      match: "/dashboard",
    },
    {
      to: "/videos",
      label: "My Videos",
      emoji: "📹",
      match: "/videos",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-soft text-foreground">
      <header className="border-b border-border/60 bg-gradient-brand/10 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/dashboard"
            className="flex items-center gap-4 text-lg font-semibold text-foreground"
          >
            <span className="flex h-14 w-14 overflow-hidden rounded-3xl border-2 border-primary/40 bg-white shadow-xl shadow-brand/40">
              <img
                src="/media/logo.png"
                alt="DoodleMagic Studio logo"
                className="h-full w-full object-cover"
              />
            </span>
            <span className="font-heading text-xl">DoodleMagic Studio</span>
          </Link>

          <div className="flex flex-wrap items-center justify-between gap-4 sm:w-auto">
            <nav className="flex flex-1 items-center gap-2 sm:gap-3">
              {navItems.map((item) => {
                const isActive = location.pathname.startsWith(item.match);
                return (
                  <Button
                    key={item.to}
                    variant={isActive ? "default" : "ghost"}
                    className={`rounded-full px-4 py-2 text-sm font-semibold ${
                      isActive
                        ? "bg-gradient-brand text-white shadow-brand"
                        : "hover:bg-primary/10"
                    }`}
                    asChild
                  >
                    <Link to={item.to} className="flex items-center gap-2">
                      <span>{item.emoji}</span>
                      <span>{item.label}</span>
                    </Link>
                  </Button>
                );
              })}
            </nav>

            <div className="flex items-center gap-3">
              <TokenBadge
                tokens={
                  loadingBalance || tokenBalance === null
                    ? "•••"
                    : Math.max(0, Number(tokenBalance))
                }
                label="magic stars"
              />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full border-border/70 bg-white/80 shadow-sm"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuContent className="min-w-[14rem]">
                    <DropdownMenuLabel>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold">
                          {displayName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {user?.email ?? "No email"}
                        </span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard">Go to dashboard</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/videos">My videos</Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin/support">Admin: Support</Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/support">Support</Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        handleLogout();
                      }}
                      disabled={isLoggingOut}
                      className="text-destructive hover:text-destructive"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      {isLoggingOut ? "Signing out…" : "Sign out"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenuPortal>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;

